import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Loader2, Clock, ChevronDown, HelpCircle, Shield, 
  Radio, Coins
} from 'lucide-react';
import { DepositStatus } from './types';

interface WaitingStepProps {
  amount: number;
  credits: number;
  status: DepositStatus;
  onSubmitTxHash: (txHash: string) => void;
}

export function WaitingStep({ 
  amount, 
  credits, 
  status,
  onSubmitTxHash 
}: WaitingStepProps) {
  const [showSupport, setShowSupport] = useState(false);
  const [supportTxHash, setSupportTxHash] = useState('');
  const [nextCheckTime, setNextCheckTime] = useState<number>(15);

  // Calculate next check time (every 15 minutes from the hour)
  useEffect(() => {
    const updateNextCheck = () => {
      const now = new Date();
      const minutes = now.getMinutes();
      const nextCheck = Math.ceil((minutes + 1) / 15) * 15;
      const minutesUntilCheck = nextCheck - minutes;
      setNextCheckTime(minutesUntilCheck === 0 ? 15 : minutesUntilCheck);
    };

    updateNextCheck();
    const interval = setInterval(updateNextCheck, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmitTxHash = () => {
    if (supportTxHash.trim()) {
      onSubmitTxHash(supportTxHash.trim());
      setSupportTxHash('');
      setShowSupport(false);
    }
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="gap-1.5 py-1.5 px-3">
            <Radio className="h-3 w-3 animate-pulse" />
            Pending
          </Badge>
        );
      case 'detecting':
        return (
          <Badge className="gap-1.5 py-1.5 px-3 bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30">
            <Loader2 className="h-3 w-3 animate-spin" />
            Detecting Transaction
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1.5 py-1.5 px-3">
            <Radio className="h-3 w-3 animate-pulse" />
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-5">
      {/* Main Status Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-8 text-center space-y-4">
          {/* Animated Loader */}
          <div className="relative mx-auto w-20 h-20">
            <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
            <Clock className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 text-primary" />
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-xl">Waiting for Blockchain Confirmation</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Your deposit has been submitted. We automatically check for new USDT (TRC20) transactions every 15 minutes.
            </p>
          </div>

          {/* Status Badge */}
          <div className="pt-2">
            {getStatusBadge()}
          </div>
        </CardContent>
      </Card>

      {/* Deposit Details */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-muted/30">
          <CardContent className="py-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Expected Amount</p>
            <p className="font-semibold text-lg">{amount} USDT</p>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="py-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Credits to Add</p>
            <p className="font-semibold text-lg flex items-center justify-center gap-1">
              <Coins className="h-4 w-4 text-primary" />
              {credits.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Timing Info */}
      <div className="bg-muted/50 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Typical confirmation time:</span>
          <span className="font-medium">5–20 minutes</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Next automatic check:</span>
          <span className="font-medium text-primary">in {nextCheckTime} min</span>
        </div>
      </div>

      {/* Trust Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Shield className="h-3.5 w-3.5 text-primary" />
        <span>Verified via TronGrid blockchain monitoring</span>
      </div>

      {/* Info */}
      <p className="text-xs text-center text-muted-foreground">
        You can close this dialog. We'll notify you when your credits are added.
      </p>

      {/* Support Section */}
      <Collapsible open={showSupport} onOpenChange={setShowSupport}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full text-muted-foreground hover:text-foreground">
            <HelpCircle className="h-4 w-4 mr-2" />
            Having problems?
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform duration-200 ${showSupport ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-3 pt-3">
          <p className="text-xs text-muted-foreground">
            If your deposit is taking longer than expected, you can provide your transaction hash (TXID) for manual review:
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Enter your TXID (optional)"
              value={supportTxHash}
              onChange={(e) => setSupportTxHash(e.target.value)}
              className="text-xs"
            />
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSubmitTxHash}
              disabled={!supportTxHash.trim()}
            >
              Submit
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
